import {
  CentricAccount,
  CentricAccountType,
  GetCentricAccountsRequest,
} from '@amzn/aac-chrome-internal-api';
import * as React from 'react';
import { CentricAccountTypeFilter } from '../IA2Types';
import { postRequestWithRetry } from 'src/commons/utils/asyncRequest';
import { logger } from 'src/commons/utils/logger';
import { centricAccountTypesWithOrder } from 'src/commons/utils/helpers';

const centricAccountsEndpoint = '/ccx/ajax/getCentricAccounts';
const header: Record<string, string> = {
  'Content-type': 'application/json; charset=UTF-8',
};
type UseCentricAccountDropdownSearchProps = {
  centricAccounts: CentricAccount[];
  centricNextToken?: string;
};

export type UseCentricAccountDropdownSearchResponse = {
  searchValue: string;
  accounts: CentricAccount[];
  nextToken: string;
  accountTypeFilterList: CentricAccountTypeFilter[];
  handleSearchChange: (...args) => void;
  setSearchInputRef: (...args) => void;
  onSearchSubmit: (...args) => void;
  onFilter: (filters: CentricAccountTypeFilter[]) => void;
  onNextPage: () => void;
};

export const useCentricAccountDropdownSearch = ({
  centricAccounts,
  centricNextToken = '',
}: UseCentricAccountDropdownSearchProps): UseCentricAccountDropdownSearchResponse => {
  const [accounts, setAccounts] = React.useState<CentricAccount[]>(
    centricAccounts || [],
  );
  const [nextToken, setNextToken] = React.useState<string>(centricNextToken);
  const selectedFilters = React.useRef<CentricAccountType[]>([]);
  const [searchValue, setSearchValue] = React.useState('');
  const searchInputRef = React.useRef<any>();
  const [accountTypeFilterList, setAccountTypeFilterList] = React.useState(
    centricAccounts.map((account) => {
      return {
        label: account.accountType, // TODO: add translations for AccountType labels,
        type: account.accountType,
        isSelected: true, // default everything to selected
      };
    }),
  );

  const setNewAccountTypeFilterList = (oldAccountTypeFilterList) => {
    return oldAccountTypeFilterList.map((accountTypeFilter) => {
      accountTypeFilter.isSelected = !selectedFilters.current.includes(
        accountTypeFilter.type,
      );
      return accountTypeFilter;
    });
  };

  const handleSearchAndFilter = async (request: GetCentricAccountsRequest) => {
    try {
      const { data } = await postRequestWithRetry({
        url: centricAccountsEndpoint,
        timeout: 10000,
        header,
        data: request,
      });
      if (request.nextToken) {
        const hasSearchOrFilter =
          selectedFilters.current.length > 0 || searchValue;
        setAccounts((accounts) => [
          ...(hasSearchOrFilter ? accounts : centricAccounts),
          ...data.accounts,
        ]);
      } else {
        setAccounts(data.accounts);
      }
      setNextToken(data?.nextToken ?? '');
    } catch (error: any) {
      logger.error(
        `Fail to fetch centricAccounts in handleSearchAndFilter with error ${error}`,
      );
    }
  };

  const setSearchInputRef = (element) => {
    searchInputRef.current = element;
  };

  const handleSearchChange = ({ target }) => {
    setSearchValue(target.value || '');
    searchInputRef?.current?.focus();
    if (!target.value) {
      setAccounts(centricAccounts);
      setNextToken(centricNextToken);
    }
  };

  const onSearchSubmit = async () => {
    if (!searchValue) {
      setAccounts(centricAccounts);
      setNextToken(centricNextToken);
    } else {
      handleSearchAndFilter({ searchKeyword: searchValue });
    }
  };

  const onFilter = (accountTypes: CentricAccountTypeFilter[]) => {
    selectedFilters.current = accountTypes
      .filter((accountType) => accountType.isSelected)
      .map((filter) => filter.type);
    setAccountTypeFilterList(setNewAccountTypeFilterList);
    if (
      selectedFilters.current.length === centricAccountTypesWithOrder.length
    ) {
      setAccounts(centricAccounts);
      setNextToken(centricNextToken);
    } else {
      handleSearchAndFilter({ accountTypes: selectedFilters.current });
    }
  };

  const onNextPage = () => {
    if (nextToken) {
      handleSearchAndFilter({ nextToken: nextToken });
    }
  };

  return {
    handleSearchChange,
    setSearchInputRef,
    onSearchSubmit,
    onFilter,
    onNextPage,
    searchValue,
    accounts,
    nextToken,
    accountTypeFilterList,
  };
};
